import * as math from "mathjs";

export const MATH_UNITS = [
    {
        name: "percent",
        definition: "0.01",
    },
    {
        name: "percentRH",
        definition: "1 cm",
    },
    {
        name: "LPM",
        definition: "1 L/min",
    },
    {
        name: "LPH",
        definition: "1 L/hour",
    },
    {
        name: "GPM",
        definition: "1 gal/min",
    },
    {
        name: "GPH",
        definition: "1 gal/hour",
    },
    {
        name: "PSI",
        definition: "1 psi",
    },
    {
        name: "percentVic",
        definition: "0.01", // 1% = 0.01
    },
    // Dimensionless so can convert to Hz by multiplying by 60: 1 Hz = 60 pulse/min
    {
        name: "pulse",
        definition: "1",
    },
    // Dimensionless placeholder for parameters that have no meaningful unit
    {
        name: "dimensionless",
        definition: "1",
    },
    // Normalized Difference Vegetation Index — dimensionless index (-1 to 1)
    {
        name: "NDVI",
        definition: "1",
    },
    // Inches of mercury — not a built-in mathjs unit, unlike its metric counterpart hPa/kPa
    {
        name: "inHg",
        definition: "3.386389 kPa",
    },
    // Acre-foot — not a built-in mathjs unit; 43,560 ft³ in international feet
    {
        name: "acreFoot",
        definition: "1233.48183754752 m^3",
    },
    // UV Index — dimensionless index (0 to 11+)
    {
        name: "UVIndex",
        definition: "1",
    },
] as const;

/**
 * Reusable function to initialize math units with a given math instance.
 * This allows the same logic to be used across different packages that have
 * different mathjs instances.
 */
export function initializeUnitsWithMath(mathInstance: typeof math) {
    MATH_UNITS.forEach((unit) => {
        // only create the new unit if it doesn't already exist
        if (!isUnitInitializedWithMath(unit.name, mathInstance)) {
            // if the unit definition is undefined, it means it is a new base unit
            if (unit.definition === undefined) {
                mathInstance.createUnit(unit.name);
            } else {
                mathInstance.createUnit(unit.name, unit.definition);
            }
        }
    });
}

/**
 * Reusable function to check if a unit is initialized with a given math instance.
 */
function isUnitInitializedWithMath(unitString: string, mathInstance: typeof math): boolean {
    // math.evaluate will error if something is not a valid unit
    try {
        mathInstance.evaluate(unitString);
        return true;
    } catch (error) {
        return false;
    }
}

/**
 * Initializes the math units for verdi-common.
 * Uses the local mathjs instance.
 */
export function initializeUnits() {
    initializeUnitsWithMath(math);
}

/**
 * Checks if a unit is initialized using the local mathjs instance.
 */
export function isUnitInitialized(unitString: string): boolean {
    return isUnitInitializedWithMath(unitString, math);
}
